/**
 * Session context + hook.
 *
 * Fetches /api/me once on mount, caches in context. Components use
 * useSession() to read the current user + role.
 *
 * Phase 1a: on 401, sets sessionStatus='unauthenticated' so callers
 * can redirect to /sign-in.
 *
 * Sub-agent IIIII (2026-07-29) — session UX orchestration surface.
 * SessionProvider is now the single home for:
 *   Track 1: silent refresh timer that fires ~5min before Neon Auth's
 *            session cookie expires, forcing the server-side rotation
 *            path (Push 5-post-smoke-45) to extend the session.
 *   Track 2: visibility-change probe (idle tab regains focus + >15min
 *            since last successful fetch → re-hit /api/me).
 *   Track 3: BroadcastChannel('olytic-session') wire — every tab hears
 *            sign-in / sign-out / refresh events from every other tab
 *            in the same origin, so a sign-out in one tab instantly
 *            invalidates every open tab.
 * All three tracks converge on the same primitive: bump `version` to
 * re-fetch /api/me, which pipes back through the auth stack and either
 * (a) rotates the cookie (happy path) or (b) 401s → flips to
 * unauthenticated → shell redirects to /sign-in with `?next=<path>`.
 */

import { createContext, type ReactNode, useContext, useEffect, useRef, useState } from 'react';

import { ApiError, fetchCurrentUser } from '../lib/api.ts';
import { clearBrandCache } from '../lib/brand-cache.ts';
import type { CurrentUser } from '../lib/types.ts';

type SessionStatus = 'loading' | 'authenticated' | 'unauthenticated' | 'error';

interface SessionContextValue {
  status: SessionStatus;
  user: CurrentUser | null;
  refresh: () => void;
  /**
   * Push #88 — direct session hydration from a response body, skipping
   * the /api/me fetch. Used by signup / accept-invite / signin handlers
   * that receive the user identity inline in their success response —
   * we hydrate state BEFORE navigating so the next page doesn't fire
   * /api/me while the browser is still persisting the Set-Cookie. Fixes
   * the first-load 401 race that previously required a refresh to
   * recover from.
   */
  hydrate: (user: CurrentUser) => void;
  /**
   * Push 4-1d-n Item D — partial in-place merge into the session user.
   * Used by /profile edit handlers that already know the persisted value
   * (display_name, timezone, confirm_writes_in_chat, etc.) after a
   * successful PATCH. Avoids the `refresh()` round-trip which flips
   * status to 'loading' and unmounts the authenticated tree, producing
   * a page-reload feel. If `user` is null (unlikely at edit time), this
   * is a no-op.
   */
  patch: (patch: Partial<CurrentUser>) => void;
  /**
   * Sub-agent IIIII (2026-07-29) — Track 3 sign-out broadcast. Callers
   * (TopNav / GlobalSearch / ProfileTab) invoke this AFTER a successful
   * /api/signout so every other open tab in this origin hears
   * `signed_out` on the BroadcastChannel and flips its own status to
   * 'unauthenticated' without needing to wait for its next fetch to
   * 401. No-op if BroadcastChannel isn't supported (Safari <15 legacy).
   */
  broadcastSignOut: () => void;
}

const SessionContext = createContext<SessionContextValue | undefined>(undefined);

// Push 4-1c-f (2026-07-01) — Idle-tab refresh threshold. When the tab
// regains visibility AND at least this many ms have passed since the
// last successful /api/me fetch, silently re-fetch to catch stale
// sessions before the user tries an action. Neon Auth's cookie is
// HttpOnly; the SPA can't check its expiry directly, so we lean on
// /api/me: if the cookie has since expired, the endpoint 401s +
// SessionProvider flips to 'unauthenticated', route guard redirects
// to /sign-in.
const IDLE_REFRESH_THRESHOLD_MS = 15 * 60 * 1_000; // 15 minutes

// Sub-agent IIIII (2026-07-29) — Track 1. Silent refresh buffer.
// Schedule a refresh() this far ahead of the session's declared expiry
// so the rotation completes before the cookie dies. 5 minutes is wide
// enough to survive a slow /api/me + isolate cold-start, narrow enough
// that the refresh is close-enough to expiry that we're not spamming
// /api/me all day. Any authenticated /api/me call refreshes the cookie
// via the Push 5-post-smoke-45 `stashRotatedSessionCookie` path.
const SILENT_REFRESH_BUFFER_MS = 5 * 60 * 1_000; // 5 minutes

// Sub-agent IIIII (2026-07-29) — Track 1 floor. Never schedule the
// refresh timer for less than this delay — protects against runaway
// tight-loop refreshes when the server clock is off, or when the
// buffer would push the timer to a negative delay. 30s is short enough
// to still catch a stale-but-close-to-expiry session, long enough that
// a bad expiry value can't cause a refresh storm.
const SILENT_REFRESH_MIN_DELAY_MS = 30 * 1_000; // 30 seconds

// Sub-agent IIIII (2026-07-29) — Track 1 cap. Even if the server
// declares a session expiry weeks out, cap the scheduled refresh at
// this delay so a long-lived tab doesn't hold a setTimeout for the
// full expiry window (Chrome/Firefox may throttle or drop timers on
// background tabs at that scale). At the cap we re-fetch, get a fresh
// expiry, and reschedule — bounded and predictable.
const SILENT_REFRESH_MAX_DELAY_MS = 60 * 60 * 1_000; // 1 hour

// Sub-agent IIIII (2026-07-29) — Track 3 BroadcastChannel name. Every
// tab / iframe on workspace.olytic.com joins this channel.
// Event shape: { type: 'signed_in' | 'signed_out' | 'refreshed', at: number }.
const SESSION_BROADCAST_CHANNEL = 'olytic-session';

interface SessionBroadcastMessage {
  type: 'signed_in' | 'signed_out' | 'refreshed';
  /** ms since epoch. Used to dedupe fast-fire echoes across tabs. */
  at: number;
}

/**
 * Sub-agent IIIII (2026-07-29) — Track 3 broadcaster. Best-effort — we
 * silently no-op when BroadcastChannel isn't available (Safari <15 legacy)
 * OR when the channel object is missing / closed. Cross-tab sync is a
 * UX polish, not a correctness requirement; the visibility probe +
 * silent-refresh timer provide the same coverage on a longer horizon.
 */
function broadcastSessionEvent(type: SessionBroadcastMessage['type']): void {
  if (typeof BroadcastChannel === 'undefined') return;
  try {
    const ch = new BroadcastChannel(SESSION_BROADCAST_CHANNEL);
    const msg: SessionBroadcastMessage = { type, at: Date.now() };
    ch.postMessage(msg);
    // Close after send — a persistent channel keeps the port alive
    // and can leak on hot-reload. Each broadcast opens + closes.
    ch.close();
  } catch {
    /* best-effort */
  }
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<SessionStatus>('loading');
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [version, setVersion] = useState(0);
  const lastFetchAtRef = useRef<number>(Date.now());
  // Sub-agent IIIII (2026-07-29) — Track 1 timer handle. Kept in a ref
  // so we can clear it on user change / unmount without stale closure
  // surprises. Set on every successful /api/me fetch that carries a
  // session_expires_at value.
  const silentRefreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');

    // Phase 1.36 (Ean dogfood 2026-06-30): client-side fail-closed timeout.
    // Belt-and-suspenders with the server-side 5s AbortSignal.timeout on
    // requireAuthContext's /get-session + getSecret upstream fetches.
    //
    // Sub-agent HHHHH (2026-07-29): tightened 12s → 3s.
    //
    // Sub-agent NO-ABORT (2026-08-13): RELAXED 3s → 15s per Ean's
    // "slow is better than cancel" directive. 3s was aborting genuine
    // cold-Neon-compute session probes and force-redirecting the user
    // to /sign-in?expired=1 when they were actually still signed in.
    // 15s gives the /api/me → /get-session → SET LOCAL round-trip room
    // to complete on cold compute (~2-8s p95 observed) without dumping
    // an authenticated user back onto the sign-in page. Still a
    // user-noticeable ceiling — beyond 15s a real stall exists and the
    // fallback-to-unauthenticated path is the right recovery.
    const SESSION_FETCH_BUDGET_MS = 15_000;
    let timeoutFired = false;
    const timeoutId = setTimeout(() => {
      if (cancelled) return;
      timeoutFired = true;
      setStatus('unauthenticated');
      setUser(null);
    }, SESSION_FETCH_BUDGET_MS);

    fetchCurrentUser()
      .then((u) => {
        if (cancelled || timeoutFired) return;
        clearTimeout(timeoutId);
        setUser(u);
        setStatus('authenticated');
        lastFetchAtRef.current = Date.now();
      })
      .catch((err: unknown) => {
        if (cancelled || timeoutFired) return;
        clearTimeout(timeoutId);
        // Sub-agent HHHHH — flatten every non-happy outcome to
        // 'unauthenticated'. 401 (real expiry), network error, non-401
        // 5xx — all lead the shell to redirect to /sign-in?expired=1
        // and let the user re-authenticate in one hop. Silent recovery
        // beats a "sign in again" callout that needs a click.
        if (err instanceof ApiError && err.status === 401) {
          setStatus('unauthenticated');
          setUser(null);
        } else {
          setStatus('unauthenticated');
          setUser(null);
        }
      });
    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [version]);

  // Push 4-1c-f (2026-07-01) — idle-tab refresh. Tab regains focus
  // AND >15 min since last successful fetch → bump version to
  // re-fetch /api/me. If Neon cookie expired in the meantime, the
  // endpoint 401s + status flips to 'unauthenticated' + route guard
  // redirects to /sign-in. Invisible to the user in the happy path.
  useEffect(() => {
    function onVisible() {
      if (document.visibilityState !== 'visible') return;
      if (status !== 'authenticated') return;
      const idleFor = Date.now() - lastFetchAtRef.current;
      if (idleFor < IDLE_REFRESH_THRESHOLD_MS) return;
      setVersion((v) => v + 1);
    }
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [status]);

  // Sub-agent IIIII (2026-07-29) — Track 1 silent-refresh timer.
  //
  // Schedule a background refresh() for SILENT_REFRESH_BUFFER_MS
  // before the session's declared expiry. On fire, refresh() bumps
  // `version` → re-mounts the fetch effect above → hits /api/me →
  // server-side auth-context.ts rotates the cookie via Push 5-post-
  // smoke-45's stash path → _middleware.ts appends Set-Cookie on the
  // response. Result: an idle tab silently extends its session forever
  // as long as the user's session hasn't been revoked upstream.
  //
  // Timer only runs on authenticated status + when the server declared
  // a session_expires_at. Cleared on status change / user change /
  // unmount so no stale timers pile up.
  useEffect(() => {
    // Always clear any prior timer first — deps change should always
    // yield exactly one active timer at any time.
    if (silentRefreshTimerRef.current !== null) {
      clearTimeout(silentRefreshTimerRef.current);
      silentRefreshTimerRef.current = null;
    }
    if (status !== 'authenticated' || user === null) return;
    const expiresAtRaw = user.session_expires_at;
    if (expiresAtRaw === null || expiresAtRaw === '') return;
    const expiresAtMs = Date.parse(expiresAtRaw);
    if (Number.isNaN(expiresAtMs)) return;
    const now = Date.now();
    const untilExpiry = expiresAtMs - now;
    // Clamp the delay: subtract the buffer, floor at MIN, ceiling at MAX.
    const rawDelay = untilExpiry - SILENT_REFRESH_BUFFER_MS;
    const clamped = Math.max(
      SILENT_REFRESH_MIN_DELAY_MS,
      Math.min(SILENT_REFRESH_MAX_DELAY_MS, rawDelay),
    );
    silentRefreshTimerRef.current = setTimeout(() => {
      silentRefreshTimerRef.current = null;
      setVersion((v) => v + 1);
    }, clamped);
    return () => {
      if (silentRefreshTimerRef.current !== null) {
        clearTimeout(silentRefreshTimerRef.current);
        silentRefreshTimerRef.current = null;
      }
    };
  }, [status, user]);

  // Sub-agent IIIII (2026-07-29) — Track 3 BroadcastChannel listener.
  //
  // Every tab in this origin joins 'olytic-session'. Events:
  //   signed_out   → status becomes 'unauthenticated'. UserShell /
  //                  AdminShell redirect happens naturally on next
  //                  render; no navigate() here (we're at provider
  //                  level, above router).
  //   signed_in    → bump version to hydrate our own state via /api/me.
  //   refreshed    → informational; no-op today (the sender already
  //                  rotated its cookie server-side, but each tab has
  //                  its own /api/me lifecycle — safer to let each
  //                  tab observe its own expiry rather than trust
  //                  cross-tab timing).
  //
  // Loop guard: broadcastSessionEvent uses a fresh channel per send,
  // so the sender does NOT receive its own message (BroadcastChannel
  // spec: postMessage does not fire onmessage on the same instance).
  // Each tab's SessionProvider listens on a DIFFERENT channel instance,
  // so cross-tab delivery works. No additional dedupe needed.
  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return;
    let ch: BroadcastChannel;
    try {
      ch = new BroadcastChannel(SESSION_BROADCAST_CHANNEL);
    } catch {
      return;
    }
    function onMessage(ev: MessageEvent<SessionBroadcastMessage>) {
      const data = ev.data;
      if (data === null || typeof data !== 'object') return;
      if (data.type === 'signed_out') {
        // Cancel any in-flight timer + flip to unauthenticated. The
        // shell (UserShell / AdminShell) picks up the change on the
        // next render and redirects to /sign-in?expired=1.
        if (silentRefreshTimerRef.current !== null) {
          clearTimeout(silentRefreshTimerRef.current);
          silentRefreshTimerRef.current = null;
        }
        // Sub-agent KKKKK Track 1 (2026-07-29) — drop cached brand in
        // every tab that heard the sign-out, so the next sign-in on
        // this browser doesn't first-paint the previous tenant's
        // brand from a lingering cache in another tab.
        clearBrandCache();
        setStatus('unauthenticated');
        setUser(null);
        return;
      }
      if (data.type === 'signed_in') {
        // Another tab just signed in — hydrate our own state.
        setVersion((v) => v + 1);
        return;
      }
      // 'refreshed' — informational only for now. Each tab's own
      // silent-refresh timer handles its own cookie rotation.
    }
    ch.addEventListener('message', onMessage);
    return () => {
      try {
        ch.removeEventListener('message', onMessage);
        ch.close();
      } catch {
        /* best-effort */
      }
    };
    // Intentional empty deps — listener lifecycle matches provider
    // mount, not status. Handlers close over setState functions which
    // are stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <SessionContext.Provider
      value={{
        status,
        user,
        refresh: () => setVersion((v) => v + 1),
        hydrate: (nextUser: CurrentUser) => {
          // Direct hydration: set state synchronously from the response
          // body. Does NOT bump version (which would trigger the
          // useEffect to re-fetch /api/me — exactly the race we're
          // avoiding). The /home page reads from the in-memory user
          // state we just set; no network fetch until the user
          // explicitly calls refresh() or until the page reloads
          // and the cookie is by then persisted.
          setUser(nextUser);
          setStatus('authenticated');
          // Sub-agent IIIII — broadcast sign-in so other tabs hydrate
          // their own /api/me too. Best-effort; safe if channel is
          // absent.
          broadcastSessionEvent('signed_in');
        },
        patch: (partial: Partial<CurrentUser>) => {
          // Push 4-1d-n Item D — partial in-place merge. No status
          // change, no network fetch, no version bump. Callers must
          // pass only fields they've just persisted; anything else
          // will race with the DB.
          setUser((prev) => (prev === null ? prev : { ...prev, ...partial }));
        },
        broadcastSignOut: () => {
          // Sub-agent IIIII (2026-07-29) — Track 3. Called by TopNav /
          // GlobalSearch / ProfileTab after their local signOut()
          // resolves. Signals every other tab to flip to
          // 'unauthenticated' immediately, no round-trip needed.
          broadcastSessionEvent('signed_out');
          // Sub-agent KKKKK Track 1 (2026-07-29) — drop cached brand so
          // the next signin on this browser (potentially a different
          // workspace) doesn't first-paint the previous tenant's brand.
          clearBrandCache();
        },
      }}
    >
      {children}
    </SessionContext.Provider>
  );
}

export function useSession(): SessionContextValue {
  const ctx = useContext(SessionContext);
  if (ctx === undefined) {
    throw new Error('useSession must be used inside <SessionProvider>');
  }
  return ctx;
}
